import { useState, useCallback, useRef, useEffect } from 'react'
import type {
  DocumentUpload,
  ProcessingJob,
  ProcessRequest,
  InvoiceExtraction,
  ExtractionHistory,
  ModelOption
} from '@/types/invoiceAnalyser'

// Get API URL from environment or default to relative path (same domain)
const API_URL = import.meta.env.VITE_ANALYSER_API_URL || ''

/**
 * Custom hook for Invoice Analyser API interactions
 */
export function useInvoiceAnalyser() {
  const [isUploading, setIsUploading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentJob, setCurrentJob] = useState<ProcessingJob | null>(null)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Upload a document
   */
  const uploadDocument = useCallback(async (file: File): Promise<DocumentUpload> => {
    setIsUploading(true)

    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await fetch(`${API_URL}/v1/documents`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to upload document')
      }

      const result = await response.json()
      return result as DocumentUpload
    } finally {
      setIsUploading(false)
    }
  }, [])

  /**
   * Start processing a document
   */
  const processDocument = useCallback(async (
    documentId: string,
    options?: ProcessRequest
  ): Promise<ProcessingJob> => {
    setIsProcessing(true)

    try {
      const response = await fetch(`${API_URL}/v1/documents/${documentId}/process`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(options || {})
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to start processing')
      }

      const result = await response.json()
      const job = result as ProcessingJob
      setCurrentJob(job)
      return job
    } finally {
      setIsProcessing(false)
    }
  }, [])

  /**
   * Get job status
   */
  const getJobStatus = useCallback(async (jobId: string): Promise<ProcessingJob> => {
    const response = await fetch(`${API_URL}/v1/jobs/${jobId}`, { credentials: 'include' })

    if (!response.ok) {
      throw new Error('Failed to get job status')
    }

    return await response.json() as ProcessingJob
  }, [])

  /**
   * Poll job status until completion
   */
  const pollJobStatus = useCallback((
    jobId: string,
    onUpdate: (job: ProcessingJob) => void,
    interval: number = 1000
  ): Promise<ProcessingJob> => {
    return new Promise((resolve, reject) => {
      // Clear any existing polling
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }

      const poll = async () => {
        try {
          const job = await getJobStatus(jobId)
          onUpdate(job)
          setCurrentJob(job)

          if (job.status === 'completed') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current)
              pollingRef.current = null
            }
            resolve(job)
          } else if (job.status === 'failed') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current)
              pollingRef.current = null
            }
            reject(new Error(job.error || 'Processing failed'))
          }
        } catch (error) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }
          reject(error)
        }
      }

      pollingRef.current = setInterval(poll, interval)
      poll() // Initial call
    })
  }, [getJobStatus])

  /**
   * Get extraction result
   */
  const getResult = useCallback(async (documentId: string): Promise<InvoiceExtraction> => {
    const response = await fetch(`${API_URL}/v1/documents/${documentId}/result`, { credentials: 'include' })

    if (!response.ok) {
      throw new Error('Failed to get result')
    }

    const data = await response.json()
    return data.result as InvoiceExtraction
  }, [])

  /**
   * Get audit trail
   */
  const getAudit = useCallback(async (documentId: string): Promise<{
    documentId: string
    job: ProcessingJob
    evidence: any
    warnings: string[]
  }> => {
    const response = await fetch(`${API_URL}/v1/documents/${documentId}/audit`, { credentials: 'include' })

    if (!response.ok) {
      throw new Error('Failed to get audit trail')
    }

    return await response.json()
  }, [])

  /**
   * Get history (all documents)
   */
  const getHistory = useCallback(async (): Promise<ExtractionHistory[]> => {
    const response = await fetch(`${API_URL}/v1/documents`, { credentials: 'include' })

    if (!response.ok) {
      throw new Error('Failed to get history')
    }

    return await response.json() as ExtractionHistory[]
  }, [])

  /**
   * Delete a document
   */
  const deleteDocument = useCallback(async (documentId: string): Promise<void> => {
    const response = await fetch(`${API_URL}/v1/documents/${documentId}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error('Failed to delete document')
    }
  }, [])

  /**
   * Get available models
   */
  const getModels = useCallback(async (): Promise<{
    ocrModels: ModelOption[]
    extractionModels: ModelOption[]
  }> => {
    const response = await fetch(`${API_URL}/v1/models`, { credentials: 'include' })

    if (!response.ok) {
      throw new Error('Failed to get models')
    }

    return await response.json()
  }, [])

  /**
   * Upload and process in one go
   */
  const uploadAndProcess = useCallback(async (
    file: File,
    options?: ProcessRequest,
    onProgress?: (job: ProcessingJob) => void
  ): Promise<{ result: InvoiceExtraction; documentId: string }> => {
    // Upload
    const uploadResult = await uploadDocument(file)

    // Start processing
    const job = await processDocument(uploadResult.documentId, options)

    // Poll until complete
    const finalJob = await pollJobStatus(job.jobId, onProgress || (() => {}))

    if (finalJob.result) {
      return {
        result: finalJob.result,
        documentId: uploadResult.documentId
      }
    }

    throw new Error('Processing completed without result')
  }, [uploadDocument, processDocument, pollJobStatus])

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  /**
   * Clear currently displayed job
   */
  const resetCurrentJob = useCallback(() => {
    setCurrentJob(null)
  }, [])

  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [stopPolling])

  /**
   * Get supplier whitelist
   */
  const getSupplierWhitelist = useCallback(async (): Promise<import('@/types').SupplierWhitelist> => {
    const response = await fetch(`${API_URL}/v1/whitelist/suppliers`, { credentials: 'include' })
    if (!response.ok) {
      throw new Error('Failed to get supplier whitelist')
    }
    return await response.json()
  }, [])

  /**
   * Add supplier to whitelist
   */
  const addSupplierToWhitelist = useCallback(async (supplier: import('@/types').WhitelistEntry): Promise<void> => {
    const response = await fetch(`${API_URL}/v1/whitelist/suppliers/add`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supplier)
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to add supplier to whitelist')
    }
  }, [])

  /**
   * Delete supplier from whitelist
   */
  const deleteSupplierFromWhitelist = useCallback(async (id: string): Promise<void> => {
    const response = await fetch(`${API_URL}/v1/whitelist/suppliers/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    if (!response.ok) {
      throw new Error('Failed to delete supplier from whitelist')
    }
  }, [])

  /**
   * Update entire supplier whitelist
   */
  const updateSupplierWhitelist = useCallback(async (suppliers: import('@/types').WhitelistEntry[]): Promise<void> => {
    const response = await fetch(`${API_URL}/v1/whitelist/suppliers`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ suppliers })
    })
    if (!response.ok) {
      throw new Error('Failed to update supplier whitelist')
    }
  }, [])

  /**
   * Get recipient whitelist
   */
  const getRecipientWhitelist = useCallback(async (): Promise<import('@/types').RecipientWhitelist> => {
    const response = await fetch(`${API_URL}/v1/whitelist/recipients`, { credentials: 'include' })
    if (!response.ok) {
      throw new Error('Failed to get recipient whitelist')
    }
    return await response.json()
  }, [])

  /**
   * Add recipient to whitelist
   */
  const addRecipientToWhitelist = useCallback(async (recipient: import('@/types').WhitelistEntry): Promise<void> => {
    const response = await fetch(`${API_URL}/v1/whitelist/recipients/add`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(recipient)
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to add recipient to whitelist')
    }
  }, [])

  /**
   * Delete recipient from whitelist
   */
  const deleteRecipientFromWhitelist = useCallback(async (id: string): Promise<void> => {
    const response = await fetch(`${API_URL}/v1/whitelist/recipients/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    if (!response.ok) {
      throw new Error('Failed to delete recipient from whitelist')
    }
  }, [])

  /**
   * Update entire recipient whitelist
   */
  const updateRecipientWhitelist = useCallback(async (recipients: import('@/types').WhitelistEntry[]): Promise<void> => {
    const response = await fetch(`${API_URL}/v1/whitelist/recipients`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipients })
    })
    if (!response.ok) {
      throw new Error('Failed to update recipient whitelist')
    }
  }, [])

  return {
    isUploading,
    isProcessing,
    currentJob,
    uploadDocument,
    processDocument,
    getJobStatus,
    pollJobStatus,
    getResult,
    getAudit,
    getHistory,
    deleteDocument,
    getModels,
    uploadAndProcess,
    stopPolling,
    resetCurrentJob,
    getSupplierWhitelist,
    addSupplierToWhitelist,
    deleteSupplierFromWhitelist,
    updateSupplierWhitelist,
    getRecipientWhitelist,
    addRecipientToWhitelist,
    deleteRecipientFromWhitelist,
    updateRecipientWhitelist
  }
}
